import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { BRAND, CONTACT, telHref } from "@/data/content";
import { Reveal, EASE } from "@/components/Reveal";
import { WhatsAppButton, CallButton } from "@/components/WhatsApp";

const NAVIGATE = [
  { label: "About", path: "/about", testid: "footer-link-about" },
  { label: "Businesses", path: "/businesses", testid: "footer-link-businesses" },
  { label: "Careers", path: "/careers", testid: "footer-link-careers" },
  { label: "Contact", path: "/contact", testid: "footer-link-contact" },
];

const SERVICES_LINKS = [
  { label: "Premium Business Websites", path: "/services/business-websites", testid: "footer-service-business-websites" },
  { label: "Custom Web Platforms", path: "/services/custom-platforms", testid: "footer-service-custom-platforms" },
  { label: "CRM & Workflow Systems", path: "/services/crm-workflows", testid: "footer-service-crm-workflows" },
  { label: "Business Automation", path: "/services/automation", testid: "footer-service-automation" },
  { label: "Technology Advisory", path: "/services/technology-advisory", testid: "footer-service-technology-advisory" },
];

const RESOURCES = [
  { label: "Pricing", path: "/pricing", testid: "footer-resource-pricing" },
  { label: "How It Works", path: "/how-it-works", testid: "footer-resource-how-it-works" },
  { label: "Locations", path: "/locations", testid: "footer-resource-locations" },
  { label: "FAQs", path: "/faq", testid: "footer-resource-faqs" },
  { label: "Insights", path: "/insights", testid: "footer-resource-insights" },
];

const LEGAL_LINKS = [
  { label: "Terms & Conditions", path: "/terms", testid: "footer-terms-link" },
  { label: "Privacy Policy", path: "/privacy", testid: "footer-privacy-link" },
  { label: "Refund Policy", path: "/refund-policy", testid: "footer-refund-link" },
  { label: "Cancellation Policy", path: "/cancellation-policy", testid: "footer-cancellation-link" },
  { label: "Payment & Billing Policy", path: "/payment-billing-policy", testid: "footer-payment-billing-link" },
  { label: "Service Delivery Policy", path: "/service-delivery-policy", testid: "footer-service-delivery-link" },
];

const REVEAL_FAST = (delay = 0) => ({ duration: 0.65, ease: EASE, delay });

const ColumnHeading = ({ children }) => (
  <div>
    <p className="flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.24em] text-slate-100">
      <span className="h-1 w-1 rotate-45 bg-[#E7CE8B]" aria-hidden="true" />
      {children}
    </p>
    <span
      className="mt-3 block h-px w-9 bg-gradient-to-r from-[#D4AF37]/80 to-transparent"
      aria-hidden="true"
    />
  </div>
);

const FootLink = ({ to, children, testid }) => (
  <Link
    data-testid={testid}
    to={to}
    className="group inline-flex items-baseline text-[15px] font-medium leading-6 text-stone-300 transition-colors duration-500 hover:text-[#E7CE8B]"
  >
    <span className="border-b border-transparent pb-0.5 transition-all duration-500 group-hover:translate-x-0.5 group-hover:border-[#D4AF37]/60">
      {children}
    </span>
  </Link>
);

const LinkList = ({ links }) => (
  <ul className="mt-8 space-y-6">
    {links.map((l) => (
      <li key={l.path + l.label}>
        <FootLink to={l.path} testid={l.testid}>
          {l.label}
        </FootLink>
      </li>
    ))}
  </ul>
);

const ViewAllLegal = ({ className = "", testid = "footer-view-all-legal" }) => (
  <Link
    data-testid={testid}
    to="/legal"
    className={`group inline-flex items-center gap-2.5 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.22em] text-[#E7CE8B] ${className}`}
  >
    <span className="whitespace-nowrap border-b border-[#D4AF37]/40 pb-1 transition-colors duration-500 group-hover:border-[#E7CE8B]">
      View All Legal
    </span>
    <span className="transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
  </Link>
);

const ConnectBlock = ({ suffix = "" }) => {
  const tel = telHref();
  const valueCls =
    "mt-2 inline-block border-b border-transparent pb-0.5 text-[15px] font-medium text-slate-100 transition-colors duration-500 hover:border-[#E7CE8B]/70 hover:text-[#E7CE8B]";
  return (
    <div className="space-y-7">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.22em] text-stone-400">Email</p>
        <a data-testid={`footer-email${suffix}`} href={`mailto:${CONTACT.email}`} className={valueCls}>
          {CONTACT.email}
        </a>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.22em] text-stone-400">
          Phone / WhatsApp
        </p>
        <a data-testid={`footer-phone-link${suffix}`} href={tel} className={valueCls}>
          {CONTACT.phone}
        </a>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <CallButton compact testid={`call-button${suffix}`} className="min-w-[148px] justify-center" />
          <WhatsAppButton compact testid={`whatsapp-button${suffix}`} className="min-w-[148px] justify-center" />
        </div>
      </div>
    </div>
  );
};

const MobileGroup = ({ title, links, extra }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        data-testid={`footer-m-toggle-${title.toLowerCase()}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-5 text-left"
      >
        <span className="flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.22em] text-slate-100">
          <span className="h-1 w-1 rotate-45 bg-[#E7CE8B]" aria-hidden="true" />
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-stone-500 transition-transform duration-500 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <ul className="space-y-1 pb-5 pt-1">
              {links.map((l) => (
                <li key={l.path + l.label}>
                  <FootLink to={l.path} testid={`${l.testid}-m`}>
                    <span className="block py-2">{l.label}</span>
                  </FootLink>
                </li>
              ))}
              {extra && <li className="pt-3">{extra}</li>}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Footer() {
  return (
    <footer
      data-testid="corporate-footer"
      className="border-t border-white/10 bg-[#040302]"
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        {/* primary area */}
        <div className="pb-16 pt-16 sm:pt-20">
          {/* brand intro */}
          <Reveal y={14} transition={REVEAL_FAST(0)}>
            <div className="flex items-center gap-5">
              <img
                src={BRAND.logo}
                alt="Logibyte Technologies logo"
                className="h-12 w-12 rounded-full sm:h-14 sm:w-14"
              />
              <div>
                <p
                  data-testid="footer-brand-name"
                  className="font-serif text-2xl font-normal tracking-tight text-slate-100 sm:text-3xl"
                >
                  LOGIBYTE TECHNOLOGIES
                </p>
                <p className="mt-2 text-[13px] font-normal uppercase tracking-[0.28em] text-slate-300">
                  {BRAND.tagline}
                </p>
              </div>
            </div>
            <p
              data-testid="footer-description"
              className="mt-4 max-w-xl text-[15px] font-normal leading-relaxed text-stone-400"
            >
              Building and Operating Technology-Enabled Businesses Through
              Structured Systems, Thoughtful Execution and Customer-Focused
              Innovation.
            </p>
          </Reveal>

          {/* desktop: five balanced columns */}
          <div className="mt-10 hidden border-t border-white/10 pt-10 lg:grid lg:grid-cols-12 lg:gap-8">
            <Reveal y={12} transition={REVEAL_FAST(0.08)} className="lg:col-span-2">
              <nav aria-label="Footer">
                <ColumnHeading>Navigate</ColumnHeading>
                <LinkList links={NAVIGATE} />
              </nav>
            </Reveal>

            <Reveal y={12} transition={REVEAL_FAST(0.16)} className="lg:col-span-3">
              <nav aria-label="Services">
                <ColumnHeading>Services</ColumnHeading>
                <LinkList links={SERVICES_LINKS} />
              </nav>
            </Reveal>

            <Reveal y={12} transition={REVEAL_FAST(0.24)} className="lg:col-span-2">
              <nav aria-label="Resources">
                <ColumnHeading>Resources</ColumnHeading>
                <LinkList links={RESOURCES} />
              </nav>
            </Reveal>

            <Reveal y={12} transition={REVEAL_FAST(0.32)} className="lg:col-span-3">
              <div>
                <ColumnHeading>Connect</ColumnHeading>
                <div className="mt-8">
                  <ConnectBlock />
                </div>
              </div>
            </Reveal>

            <Reveal y={12} transition={REVEAL_FAST(0.4)} className="lg:col-span-2">
              <nav aria-label="Legal">
                <ColumnHeading>Legal</ColumnHeading>
                <LinkList links={LEGAL_LINKS} />
                <ViewAllLegal className="mt-8" />
              </nav>
            </Reveal>
          </div>

          {/* mobile: brand/contact first, then expandable groups */}
          <div className="mt-10 border-t border-white/10 pt-10 lg:hidden">
            <Reveal y={12} transition={REVEAL_FAST(0.1)}>
              <ConnectBlock suffix="-m" />
            </Reveal>
            <div className="mt-10 border-t border-white/10" data-testid="footer-mobile-groups">
              <MobileGroup title="Navigate" links={NAVIGATE} />
              <MobileGroup title="Services" links={SERVICES_LINKS} />
              <MobileGroup title="Resources" links={RESOURCES} />
              <MobileGroup title="Legal" links={LEGAL_LINKS} extra={<ViewAllLegal testid="footer-view-all-legal-m" />} />
            </div>
          </div>
        </div>

        {/* bottom bar */}
        <div className="flex flex-col gap-4 border-t border-white/10 py-7 sm:flex-row sm:items-center sm:justify-between sm:pr-24">
          <p data-testid="footer-copyright-text" className="text-sm font-normal leading-relaxed text-stone-400">
            {BRAND.copyright}
          </p>
          <p data-testid="footer-tagline" className="shrink-0 text-xs font-medium uppercase tracking-[0.18em] text-stone-400 sm:text-right">
            Building Businesses. Creating Value.
          </p>
        </div>
      </div>
    </footer>
  );
}
